import { useEffect, useState, useRef } from "react";
import {
  NodeListService,
  NodeCreateService,
  NodeUpdateService,
  NodeDeleteService,
} from "../services/NodeService";
import NodeList from "../modules/NodeList";
import { NodeCreate } from "../modules/NodeCreate";
import { NodeEdit } from "../modules/NodeEdit";
import { NodeDelete } from "../modules/NodeDelete";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import FilterController from "components/filter/controller/FilterController";

export default function NodeController() {
  const filterConfig = [
    { visibleName: "Nombre", dbField: "name", type: "text" },
    { visibleName: "Municipio", dbField: "municipality", type: "text" },
    { visibleName: "Estado", dbField: "active", type: "select", source: "nodeStatus" },
  ];

  const dataTable = "nodes"; // Tabla indicada
  const filterControllerRef = useRef();

  // Estados principales
  const [isDisabled, setIsDisabled] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [newData, setNewData] = useState({ name: "", municipality: "", active: true });
  const [editedData, setEditedData] = useState({ name: "", municipality: "", active: true });
  const [recoveredData, setRecoveredData] = useState({});
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [infoDelete, setInfoDelete] = useState({});
  const [nodeList, setNodeList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [isAlert, setIsAlert] = useState(false);

  // Validaciones
  const [isFieldValid, setIsFieldValid] = useState({
    name: true,
    municipality: true,
    active: true,
  });

  const validationPatterns = {
    name: /^[a-zA-ZÀ-ÿ0-9\s\-,.]+$/, // Letras, números, espacios y algunos caracteres seguros
    municipality: /^[a-zA-ZÀ-ÿ0-9\s\-,.]+$/, // Letras, números, espacios y algunos caracteres seguros
    active: /^(true|false|1|0)$/, // Booleano
  };

  const validateField = (value, pattern) => pattern.test(String(value).trim());

  const handleChangeValid = (fieldName, value) => {
    let isValid = true;
    if (validationPatterns[fieldName]) {
      isValid = validateField(value, validationPatterns[fieldName]);
    }
    setIsFieldValid((prev) => ({
      ...prev,
      [fieldName]: isValid,
    }));
  };

  const hasInvalidFields = (fields) => Object.values(fields).some((v) => v === false);

  const resetFieldValid = () => setIsFieldValid({ name: true, municipality: true, active: true });

  // Función para obtener las opciones de estado de nodos
  const getNodeStatusOptions = () => {
    return [
      { id: 1, name: "Activo" },
      { id: 0, name: "Inactivo" }
    ];
  };

  // CRUD HANDLERS

  // Listar nodos
  const requestNodeList = async (pageIndex = 1) => {
    setLoading(true);
    const result = await NodeListService(pageIndex, 10);
    if (result.success) {
      const { payload } = result;
      setNodeList(payload.data);
      setTotalPages(payload.last_page);
      setTotalData(payload.total);
      setEntriesStart(payload.from);
      setEntriesEnd(payload.to);
    } else {
      handleAlert("fail", result.message);
    }
    setLoading(false);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Crear nodo
  const handleNodeCreate = () => {
    setNewData({ name: "", municipality: "", active: true });
    resetFieldValid();
    setOpenCreate(true);
    setIsDisabled(false);
  };

  const submitNodeCreate = async (e) => {
    e.preventDefault();
    if (hasInvalidFields(isFieldValid)) {
      handleAlert("fail", "Hay al menos un error en los campos ingresados");
      return;
    }
    const response = await NodeCreateService(newData);
    if (response.success) {
      handleAlert("success", "Nodo creado correctamente");
      requestNodeList(currentPage);
      callHandleClickSubmit();
      setOpenCreate(false);
    } else {
      handleAlert("fail", response.message);
    }
  };

  // Editar nodo
  const handleNodeEdit = (event, node) => {
    event.preventDefault();
    setEditedData({ id: node.id, name: node.name, municipality: node.municipality, active: node.active }); // <-- incluye municipality
    setRecoveredData({ id: node.id, name: node.name, municipality: node.municipality, active: node.active }); // <-- incluye municipality
    resetFieldValid();
    setOpenEdit(true);
    setIsDisabled(false);
  };

  const submitNodeEdit = async (e) => {
    e.preventDefault();
    if (hasInvalidFields(isFieldValid)) {
      handleAlert("fail", "Hay al menos un error en los campos ingresados");
      return;
    }
    // El id se toma del nodo seleccionado (por ejemplo, guardado en recoveredData)
    const response = await NodeUpdateService(recoveredData.id, editedData);
    if (response.success) {
      handleAlert("success", "Nodo editado correctamente");
      requestNodeList(currentPage);
      callHandleClickSubmit();
      setOpenEdit(false);
    } else {
      handleAlert("fail", response.message);
    }
  };

  // Eliminar nodo
  const handleNodeDelete = (event, node) => {
    event.preventDefault();
    setInfoDelete(node);
    setOpenDelete(true);
  };

  const submitNodeDelete = async (e) => {
    e.preventDefault();
    const response = await NodeDeleteService(infoDelete.id);
    if (response.success) {
      handleAlert("success", "Nodo eliminado correctamente");
      requestNodeList(currentPage);
      callHandleClickSubmit();
      setOpenDelete(false);
    } else {
      handleAlert("fail", response.message);
    }
  };

  const closeNodeCreate = () => setOpenCreate(false);
  const closeNodeEdit = () => setOpenEdit(false);
  const closeNodeDelete = () => setOpenDelete(false);

  // Cambios en formularios
  const getFieldValueNew = (fieldName) => newData[fieldName];
  const getFieldValue = (fieldName) => editedData[fieldName];

  const handleFieldChangeNew = (fieldName, value) => {
    setNewData((prev) => ({ ...prev, [fieldName]: value }));
    handleChangeValid(fieldName, value);
  };

  const handleFieldChange = (fieldName, value) => {
    setEditedData((prev) => ({ ...prev, [fieldName]: value }));
    handleChangeValid(fieldName, value);
  };

  // Alertas
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  // Función para llamar a handleClickSubmit desde el componente padre
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  // Efecto para cargar la lista al montar
  useEffect(() => {
    requestNodeList(currentPage);
    // eslint-disable-next-line
  }, [currentPage]);

  // Detectar cambios en formularios
  useEffect(() => {
    const hasFormChanged = Object.keys(editedData).some(
      (fieldName) => editedData[fieldName] !== recoveredData[fieldName]
    );
    const isAnyFieldEmpty = Object.values(editedData).some((value) => value === "");
    setIsFormModified(!isAnyFieldEmpty && hasFormChanged);
  }, [editedData, recoveredData]);

  useEffect(() => {
    const requiredFields = Object.keys(newData);
    const someFieldIsEmpty = requiredFields.some((fieldName) => !newData[fieldName]);
    setIsFormModified(someFieldIsEmpty);
  }, [newData]);

  // Render
  return (
    <>
      <Spinner loading={loading} />
      <MDAlert isAlert={isAlert} setIsAlert={setIsAlert} typeAlert={typeAlert} message={message} />
      <FilterController
        ref={filterControllerRef}
        filterConfig={filterConfig}
        dataTable={dataTable}
        setTotalData={setTotalData}
        setEntriesEnd={setEntriesEnd}
        setEntriesStart={setEntriesStart}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
        setListData={setNodeList}
        handleAlert={handleAlert}
        getNodeStatusOptions={getNodeStatusOptions}
      />
      <NodeList
        nodeList={nodeList}
        handleNodeCreate={handleNodeCreate}
        handleNodeEdit={handleNodeEdit}
        handleNodeDelete={handleNodeDelete}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        handlePageChange={handlePageChange}
        totalData={totalData}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
      />
      <NodeCreate
        open={openCreate}
        handleClose={closeNodeCreate}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        getFieldValue={getFieldValueNew}
        handleFieldChange={handleFieldChangeNew}
        handleSubmit={submitNodeCreate}
        isFieldValid={isFieldValid}
      />
      <NodeEdit
        open={openEdit}
        handleClose={closeNodeEdit}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        getFieldValue={getFieldValue}
        handleFieldChange={handleFieldChange}
        handleSubmit={submitNodeEdit}
        isFieldValid={isFieldValid}
      />
      <NodeDelete
        openDelete={openDelete}
        closeDelete={closeNodeDelete}
        infoDelete={infoDelete}
        submitDelete={submitNodeDelete}
      />
    </>
  );
}
